import { useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { PointCloudMaterialProps, defaultMaxId } from '@fabric-web/3d-viewer';
import * as globalState from '@/state';
import { TRANSPARENT_COLOR } from '@/constants';
import { useColorMap } from '@/hooks/useColorMap';
import { convertColorHexStringToNumber, notEmpty } from '@/utils';
import * as state from '@/components/AssetLabelling/state';
import { usePointSize } from '@/hooks';

const DEFAULT_COLOR = 0xccccccff;

const blendedColor = (rgba: number, rgbBlendLevel: number): number => {
  // rgbBlendLevel has range [1,255]
  return (rgba & 0xffffff00) | ((256 - rgbBlendLevel) & 0xff);
};

export const useLabelAnnotationsMaterial = () => {
  const [rgbBlendLevel, setRgbBlendLevel] = useAtom(globalState.rgbBlendLevel);
  const structure = useAtomValue(state.structure);
  const labelAnnotations = useAtomValue(state.labelAnnotations);
  const labelClassById = useAtomValue(state.labelClassById);

  const colorMap = useColorMap(defaultMaxId, undefined, DEFAULT_COLOR);
  const visibilityMap = useColorMap(defaultMaxId, undefined, DEFAULT_COLOR);

  const setEnableDynamicPointSize = useSetAtom(globalState.enableDynamicPointSize);
  const setEdlStrength = useSetAtom(globalState.edlStrength);
  const { pointSize, enableSizeAttenuation } = usePointSize();

  useEffect(() => {
    setEnableDynamicPointSize(true);
    setRgbBlendLevel(215);
    setTimeout(() => {
      setEdlStrength(0);
    }, 1);
  }, [setEnableDynamicPointSize, setEdlStrength, setRgbBlendLevel]);

  useEffect(() => {
    if (!structure) return;
    if (!labelAnnotations) return;
    if (!labelClassById) return;
    // labelClassById hasn't been updated yet for the new structure?
    if (!labelAnnotations.filter(notEmpty).some(({ labelClassId }) => labelClassById[labelClassId]))
      return;

    labelAnnotations.filter(notEmpty).forEach(({ octreeId, labelClassId }) => {
      const labelClass = labelClassById[labelClassId];
      const color = labelClass.isVisible
        ? convertColorHexStringToNumber(labelClass.color)
        : TRANSPARENT_COLOR;
      visibilityMap.setColor(octreeId, color);
      colorMap.setColor(octreeId, structure?.hasRGB ? blendedColor(color, rgbBlendLevel) : color);
    });
  }, [structure, labelAnnotations, labelClassById, colorMap, visibilityMap, rgbBlendLevel]);

  const labelAnnotationsMaterial: PointCloudMaterialProps = useMemo(
    () => ({
      attributeName: 'label',
      colorMap,
      visibilityMap,
      visibilityAttributeName: 'label',
      pointSize,
      enableSizeAttenuation,
      hasColor: structure?.hasRGB ?? false,
    }),
    [colorMap, visibilityMap, structure, pointSize, enableSizeAttenuation]
  );

  return { labelAnnotationsMaterial };
};
